// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles, ITheme } from "@fluentui/react";
import type { IRawStyle, IStyle } from "@fluentui/react/lib/Styling";
import { concatStyleSets, mergeStyles } from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  ActionControlSize,
  commonHighContrastButtonRestStyles,
  getStandardControlSemanticColorsWithTheme,
} from "@1js/acui-common";
import type { AppGalleryStyles } from "@1js/acui-gallery";
import { concatAppGalleryStyles } from "@1js/acui-gallery";

import { getLargePreviewGalleryStyles } from "./largePreviewGalleryStyles";
import type { GetTableStylingCallback } from "./RibbonTableGalleryButton.Props";
import { isNextStyleEnabled } from "@1js/acui-theme";

export interface RibbonTableStylesGalleryStyles {
  tableStyles: IStyle;
  tableCellStyles: IStyle;
}

const borderWidth = 4;
const galleryItemPadding = 4;
const galleryItemPaddingNext = 2;
const galleryButtonWidth = 60;
export const tableStylesGalleryButtonWidth =
  galleryButtonWidth + borderWidth * 2 + galleryItemPadding * 2;

const previewGalleryButtonOffset = {
  padding: "2px",
};

const getPreviewGalleryButtonStyles = memoizeFunction(
  (theme: ITheme): IButtonStyles => {
    return {
      root: {
        boxSizing: "content-box",
        height: 26,
        width: 41,
        padding: isNextStyleEnabled()
          ? galleryItemPaddingNext
          : galleryItemPadding,
        borderTopWidth: 0,
        borderBottomWidth: 0,
      },
      rootFocused: previewGalleryButtonOffset,
      rootChecked: {
        selectors: {
          ":focus": previewGalleryButtonOffset,
        },
      },
      flexContainer: [
        {
          backgroundColor:
            getStandardControlSemanticColorsWithTheme(theme)
              .tableGalleryBackgroundColor,
          color:
            getStandardControlSemanticColorsWithTheme(theme)
              .tableGalleryCellColor,
        },
        commonHighContrastButtonRestStyles,
      ],
    };
  }
);

function getPassiveGalleryButtonStyles(): IRawStyle {
  return {
    boxSizing: "content-box",
    height: 41,
    width: galleryButtonWidth,
    padding: galleryItemPadding,
    borderTopWidth: borderWidth,
    borderBottomWidth: borderWidth,
    borderLeftWidth: borderWidth,
    borderRightWidth: borderWidth,
  };
}

const getFullGalleryButtonStyles = memoizeFunction(
  (theme: ITheme): IButtonStyles => {
    return {
      root: [getPassiveGalleryButtonStyles()],
      rootDisabled: {
        selectors: {
          ":hover": getPassiveGalleryButtonStyles(),
          ":active": getPassiveGalleryButtonStyles(),
        },
      },
      flexContainer: {
        backgroundColor:
          getStandardControlSemanticColorsWithTheme(theme)
            .tableGalleryBackgroundColor,
        color:
          getStandardControlSemanticColorsWithTheme(theme)
            .tableGalleryCellColor,
      },
    };
  }
);

const getPreviewExpandButtonStyles: IButtonStyles = {
  root: {
    height: 34,
    borderTop: "none",
    borderBottom: "none",
  },
};

const previewExpandButtonStylesNext: IButtonStyles = {
  root: {
    height: 30,
  },
};

const getCommonTableStyles: IStyle = {
  borderCollapse: "collapse",
  fontWeight: "bold",
  tableLayout: "fixed",
};

const getPreviewTableStyles: IStyle = {
  fontSize: 5,
  width: 40,
};

const getFullTableStyles: IStyle = {
  fontSize: 6,
  width: galleryButtonWidth,
};

const getCommonTableCellStyles: IStyle = {
  overflow: "hidden",
  padding: "0px 1px",
};

const getPreviewTableCellStyles: IStyle = {
  height: 4,
  lineHeight: 4,
  width: 5,
};

const getFullTableCellStyles: IStyle = {
  height: 7,
  lineHeight: 7,
  width: 8,
};

/**
 * Get the styling for the table inside of the preview galleryButton
 * @param { Partial<RibbonTableStylesGalleryStyles> } customStyles - Optional, custom styles to apply to the table and table cells
 */
export const getTableStylesPreviewGalleryButtonStyles = memoizeFunction(
  (
    customStyles?: Partial<RibbonTableStylesGalleryStyles>
  ): RibbonTableStylesGalleryStyles => {
    return {
      tableStyles: mergeStyles(
        getPreviewTableStyles,
        getCommonTableStyles,
        customStyles && customStyles.tableStyles
      ),
      tableCellStyles: mergeStyles(
        getPreviewTableCellStyles,
        getCommonTableCellStyles,
        customStyles && customStyles.tableCellStyles
      ),
    };
  }
);

/**
 * Get the styling for the table inside of the full galleryButton
 * @param { Partial<RibbonTableStylesGalleryStyles> } customStyles - Optional, custom styles to apply to the table and table cells
 */
export const getTableStylesFullGalleryButtonStyles = memoizeFunction(
  (
    customStyles?: Partial<RibbonTableStylesGalleryStyles>
  ): RibbonTableStylesGalleryStyles => {
    return {
      tableStyles: mergeStyles(
        getFullTableStyles,
        getCommonTableStyles,
        customStyles && customStyles.tableStyles
      ),
      tableCellStyles: mergeStyles(
        getFullTableCellStyles,
        getCommonTableCellStyles,
        customStyles && customStyles.tableCellStyles
      ),
    };
  }
);

/**
 * Get the table styling callback for the given size preview gallery.
 *
 * Large preview gallery buttons use the same styles as the full (expanded) gallery buttons.
 */
export const getSizedTableStylesPreviewGalleryButtonStylingCallback =
  memoizeFunction(
    (size: ActionControlSize | undefined): GetTableStylingCallback =>
      size === ActionControlSize.Large
        ? getTableStylesFullGalleryButtonStyles
        : getTableStylesPreviewGalleryButtonStyles
  );

/**
 * Get the styling for the preview gallery
 * @param { Partial<AppGalleryStyles> } customStyles - Optional, custom styles to apply to gallery
 */
export const getTableStylesPreviewGalleryStyles = memoizeFunction(
  (
    theme: ITheme,
    customStyles?: Partial<AppGalleryStyles>
  ): Partial<AppGalleryStyles> => {
    const galleryContainer = customStyles?.galleryContainer;
    const previewExpandButton = concatStyleSets(
      getPreviewExpandButtonStyles,
      isNextStyleEnabled() && previewExpandButtonStylesNext,
      customStyles && customStyles.previewExpandButton
    );
    return {
      ...customStyles,
      galleryContainer,
      previewExpandButton,
      galleryButtonInPreview: concatStyleSets(
        getPreviewGalleryButtonStyles(theme),
        customStyles && customStyles.galleryButtonInPreview
      ),
    };
  }
);

export const getSizedTableStylesPreviewGalleryStyles = memoizeFunction(
  (
    size: ActionControlSize | undefined,
    externalStyles: Partial<AppGalleryStyles> | undefined
  ): Partial<AppGalleryStyles> => {
    if (size === ActionControlSize.Large) {
      return concatAppGalleryStyles(
        externalStyles,
        getLargePreviewGalleryStyles()
      );
    }
    return externalStyles || {};
  }
);

/**
 * Get the styling for the full gallery
 * @param { Partial<AppGalleryStyles> } customStyles - Optional, custom styles to apply to gallery
 */
export const getTableStylesFullGalleryStyles = memoizeFunction(
  (
    theme: ITheme,
    customStyles?: Partial<AppGalleryStyles>
  ): Partial<AppGalleryStyles> => {
    return {
      galleryButtonInExpanded: concatStyleSets(
        getFullGalleryButtonStyles(theme),
        customStyles && customStyles.galleryButtonInExpanded
      ),
    };
  }
);
